import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, Row, Space } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactPlayer from 'react-player';
import { connect } from 'react-redux';
import apiPaths from 'services/apiPaths';
import ChallengeResourceWrapper, {
  ChallengeResourceWrapperBlocked,
} from 'src/components/challenge/ChallengeResourceWrapper';
import { ExperienceDetailWrapper } from 'src/components/challenge/ExperienceDetailWrapper';
import CustomModal from 'src/components/CustomModal';
import { extractVideoID } from 'src/components/staticComponents/Video';
import { IRootReducers } from 'src/reducers';
import {
  ButtonStatusEnum,
  ChallengeTypeEnum,
  ExperienceDetailStatusEnum,
} from 'src/shared/enums';
import {
  IChallengeResourceRender,
  IChallengeVideo,
  IExperienceDetailWrapper,
} from 'src/shared/models';
import { checkUserResourcePoints, completeChallengeUserResource } from 'utils';
import FormationResourceWrapper from 'src/components/challenge/formation/FormationResourceWrapper';

interface OwnProps extends IChallengeResourceRender {
  challengeType: number;
  config: Partial<IChallengeVideo>;
  blocked?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-use-before-define
type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ChallengeVideo: FC<Props> = ({
  challengeType,
  config,
  blocked,
  updateChallenge,
  updateUserPoints,
  userPoints,
  challenge,
}) => {
  const {
    buttonText,
    image: optionalImage,
    isVideoFile,
    points,
    title,
    url,
  } = config;
  const isExperience = challengeType === ChallengeTypeEnum.EXPERIENCE;
  const isFormationCon = challengeType === ChallengeTypeEnum.FORMATION_CON;

  const { locale, formatMessage } = useIntl();

  const [loading, setLoading] = useState(false);
  const [isPlayed, setIsPlayed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isYoutubeVideoActive, setIsYoutubeVideoActive] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [seeImage, setSeeImage] = useState(true);
  const domElementID = `challengeVideo-${config.idResource}`;
  const youtubeVideoId = !isVideoFile && extractVideoID(url);
  const youtubeVideoPlaceholder =
    youtubeVideoId &&
    `https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`;

  const refVideoForm = useRef(null);
  if (!config) return <React.Fragment />;

  const handlePlayVideo = () => {
    const domElement = document.getElementById(
      domElementID
    ) as HTMLVideoElement;

    switch (true) {
      case isVideoFile && !isPlayed:
        setIsPlayed(true);
        setSeeImage(false);
        domElement.play();
        break;
      case !isVideoFile && !isPlayed:
        setIsPlayed(true);
        setIsYoutubeVideoActive(true);
        document
          .getElementById(domElementID)
          .setAttribute('src', `${url}?autoplay=1`);
        break;
      case isVideoFile && isPlayed:
        setIsPlayed(false);
        domElement.pause();
        break;
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    performAction();
  };

  const handleLightPlay = () => {
    performAction();
    setIsPlaying(true);
  };

  // TODO:
  const handleFormationVideoStart = () => {
    // TODO: cómo conseguir el Challenge que sea IChallenge del Redux?

    setIsPlaying(true);
    setIsPlayed(false);
  };

  const handleFormationVideoEnd = () => {
    setIsPlaying(false);
    setIsPlayed(true);
  };

  const challengeResourceConfig = {
    idResource: config.idResource,
    updateChallenge,
    updateUserPoints,
  };

  const performAction = async () => {
    if (config.isCompleted) return;

    try {
      setLoading(true);
      const err = await completeChallengeUserResource({
        ...challengeResourceConfig,
        dataPath: isExperience ? apiPaths.CHALLENGES.COMPLETE_EXPERIENCE : null,
      });
      if (err) {
        const stringError: string = formatMessage({
          id: 'page.experience.error-video',
        });
        setError(stringError);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsPlaying(false);
    setModalVisible(false);
    setError(null);
  };

  const wrapperConfig: IExperienceDetailWrapper = {
    title,
    points,
    status: ExperienceDetailStatusEnum.IN_PROGRESS,
    button: {
      label: buttonText,
      status: ButtonStatusEnum.ENABLED,
      performAction: handleOpenModal,
    },
  };

  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    config.points,
    challengeType
  );

  const pointsLabel = Number(Math.abs(points));

  if (config.isCompleted) {
    wrapperConfig.button = {
      label: formatMessage({
        id: 'experience.button-completed',
      }),
      performAction: null,
      status: ButtonStatusEnum.DONE,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DONE;
  }

  if (config.isBlocked || !hasUserEnoughPoints) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.DISABLED,
      performAction: null,
      label: hasUserEnoughPoints ? null : buttonText,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DISABLED;
  }

  if (!config.isChallengeInDate) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.HIDDEN,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.HIDDEN;
  }

  const videoComponent = config && (
    <Row className="challengeVideo" justify="center" align="middle">
      <Row justify="center" className="challengeVideo__videoContainer">
        {!isExperience && !isFormationCon && (
          <>
            <ChallengeResourceWrapperBlocked />
            {!isYoutubeVideoActive && (
              <Row
                className="image-content"
                align="middle"
                justify="center"
                onClick={handlePlayVideo}
              >
                {!isPlayed && (
                  <img
                    alt="play"
                    className="icon icon__white icon--player icon__size-120"
                  />
                )}
              </Row>
            )}
          </>
        )}

        {isVideoFile ? (
          <>
            <video
              height="100%"
              width="100%"
              id={domElementID}
              onClick={performAction}
              className={`challengeVideo__video ${
                optionalImage ? 'with-optional-image' : ''
              }`}
            >
              <source src={url} type="video/mp4" />
            </video>
            {seeImage && optionalImage && (
              <img
                alt="love-isdin-video"
                className="video-placeholder"
                loading="lazy"
                src={optionalImage}
              />
            )}
          </>
        ) : !isExperience && !isFormationCon ? (
          <>
            <iframe
              height="100%"
              width="100%"
              allowFullScreen
              onClick={performAction}
              src={url}
              id={domElementID}
            />
            {!isYoutubeVideoActive && (
              <img
                alt="love-isdin-video"
                loading="lazy"
                className="youtube-placeholder"
                src={optionalImage || youtubeVideoPlaceholder}
              />
            )}
          </>
        ) : (
          <>
            {!blocked ? (
              <ReactPlayer
                className="challengeVideoQuiz"
                controls
                light={optionalImage || youtubeVideoPlaceholder}
                onClickPreview={
                  isFormationCon ? handleFormationVideoStart : handleLightPlay
                }
                playIcon={
                  <Row className="video-cover" gutter={3}>
                    <img
                      alt="play"
                      className="icon icon__white icon--play-circle icon__size-Challenge-Video"
                    />
                    {isPlayed && isFormationCon ? (
                      <FormattedMessage id="formation.quiz-dermo.video-cover.repeat-video" />
                    ) : isFormationCon ? (
                      <FormattedMessage id="formation.quiz-dermo.video-cover.watch-video" />
                    ) : null}
                  </Row>
                }
                playing={!isFormationCon ? modalVisible : true}
                playsinline={true}
                url={url}
                ref={refVideoForm}
                onEnded={
                  isFormationCon
                    ? () => {
                        handleFormationVideoEnd();
                        refVideoForm.current.showPreview(
                          youtubeVideoPlaceholder
                        );
                      }
                    : null
                }
              />
            ) : (
              <>
                <img
                  src={optionalImage || youtubeVideoPlaceholder}
                  className="preImageVideo"
                />
                <div className="divIconContainer">
                  <img className="icon icon__white icon--experience-lock blockedVideoIcon icon__size-Blocked-Video" />
                </div>
              </>
            )}
          </>
        )}
      </Row>
      {isVideoFile && (
        <Row className="challengeVideo__containerButton" justify="center">
          <a className="challengeVideo__containerButton--button" href={url}>
            <Button
              className="btn-ghost btn-ghost--black"
              onClick={performAction}
            >
              <Row justify="space-between" align="middle">
                <FormattedMessage id="button.download" />
                <a className={`icon icon--download`} />
              </Row>
            </Button>
          </a>
        </Row>
      )}
    </Row>
  );

  return isExperience ? (
    <>
      <ExperienceDetailWrapper {...wrapperConfig} />
      <CustomModal
        {...{
          visible: modalVisible,
          error,
          content: videoComponent,
          onCancel: handleCloseModal,
          footer: null,
          showIcon: false,
          customClass: 'experienceDetail--Video fullScreen',
        }}
      />
    </>
  ) : isFormationCon ? (
    <FormationResourceWrapper
      //description={config.description}
      isBlocked={false}
      styleClass="formationDetail--Video"
      title={title}
    >
      {videoComponent}
    </FormationResourceWrapper>
  ) : (
    <ChallengeResourceWrapper
      bottomLabel={
        config.isCompleted && {
          label: `page.challenge.congratulations-video-${
            isVideoFile ? 'completed' : 'watched'
          }`,
          style: 'completed',
        }
      }
      description={config.description}
      icon={config.icon}
      points={formatMessage(
        { id: 'page.challenge.plus-sign-{points}-points' },
        { points: pointsLabel }
      )}
      styleClass="challengeDetail--Video"
      title={title}
    >
      {videoComponent}
    </ChallengeResourceWrapper>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
    userPoints: state.auth.userPoints,
  };
};

export default connect(mapStateToProps)(ChallengeVideo);
